import type { TestExecutionStatus } from './test-case.model';
import type { TestRunStatus } from './test-run.model';

export type TestPlanStatus = 'ENTWURF' | 'AKTIV' | 'ABGESCHLOSSEN';

export const TEST_PLAN_STATUSES: readonly TestPlanStatus[] = ['ENTWURF', 'AKTIV', 'ABGESCHLOSSEN'];

export type TestPlanType = 'FREIGABE' | 'REGRESSION';

export const TEST_PLAN_TYPES: readonly TestPlanType[] = ['FREIGABE', 'REGRESSION'];

export type TestPlanExecutionMode = 'MANUAL' | 'AUTOMATED' | 'MIXED';

export type TestPlanExecutionKind = Exclude<TestPlanExecutionMode, 'MIXED'>;

export interface TestPlanProgress {
  total: number;
  passed: number;
  failed: number;
  blocked: number;
  inProgress: number;
  open: number;
}

export interface TestPlan {
  id: string;
  projectId: string;
  publicId: string | null;
  title: string;
  /**
   * Fachliche Release-ID, z. B. `2026.3`. `null` nur bei Plänen, die eine ältere
   * Backend-Instanz ohne Release angelegt hat.
   */
  releaseId: string | null;
  /** Anzeigename des Releases, z. B. `Release 2026.3`; `null` wie bei `releaseId`. */
  releaseLabel: string | null;
  status: TestPlanStatus;
  /** Fehlt bei älteren Antworten; gilt dann als `FREIGABE`. */
  type?: TestPlanType;
  dueDate: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  testCaseIds: string[];
  stepSelections?: Record<string, number[]>;
  progress: TestPlanProgress;
  /**
   * Last counted status per test case of the plan (runs of the plan or without plan, in the plan's release),
   * the same source as `progress`. Cases without a result are missing; if the field is missing, all count as open.
   */
  lastExecutionStatusByCase?: Record<string, TestExecutionStatus>;
  /** `MIXED` with test cases of both kinds, otherwise the stored kind, otherwise derived from the test cases; `null` without test cases. */
  executionMode?: TestPlanExecutionMode | null;
  /** Fehlt bei älteren Antworten; der Plan gilt dann nicht als leer. */
  packageCount?: number;
  runCount?: number;
  lastRunAt?: string | null;
  archivedAt?: string | null;
  /** Fehlt bei älteren Antworten; gilt dann als keine Tags. */
  tags?: string[];
  /** Fehlt bei älteren Antworten; gilt dann als keine Regel. */
  selectionRuleTags?: string[];
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTestPlanRequest {
  title: string;
  releaseId: string;
  status?: TestPlanStatus;
  type?: TestPlanType;
  dueDate?: string | null;
  assigneeUserId?: string | null;
  testCaseIds?: string[];
  stepSelections?: Record<string, number[]>;
  tags?: string[];
  executionKind?: TestPlanExecutionKind;
  selectionRuleTags?: string[];
  notes?: string;
}

export interface UpdateTestPlanRequest {
  title?: string;
  /** Fehlt das Feld, bleibt das Release unverändert. */
  releaseId?: string;
  status?: TestPlanStatus;
  /** Fehlt das Feld, bleibt die Art unverändert. */
  type?: TestPlanType;
  dueDate?: string | null;
  assigneeUserId?: string | null;
  clearAssignee?: boolean;
  testCaseIds?: string[];
  stepSelections?: Record<string, number[]>;
  /** Fehlt das Feld, bleiben die Tags unverändert; `[]` leert sie. */
  tags?: string[];
  executionKind?: TestPlanExecutionKind;
  /** Fehlt das Feld, bleibt die Regel unverändert; `[]` entfernt sie. */
  selectionRuleTags?: string[];
  notes?: string;
  /** Nur bei geänderter Auswahl wirksam; landet im Verlauf des Plans. */
  changeReason?: string;
}

export interface TestPlanRun {
  key: string;
  trial: boolean;
  status: TestRunStatus;
  startedAt: string | null;
  counted: boolean;
  running: boolean;
}

export type TestPlanExecutionStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';

export interface TestPlanReportDetails {
  testCaseId: string;
  defectCount: number;
  steps: { total: number; passed: number; failed: number; inProgress: number; open: number };
}

export interface TestPlanExecution {
  id: string;
  testPlanId: string;
  status: TestPlanExecutionStatus;
  startedAt: string | null;
  finishedAt: string | null;
  testRunId: string | null;
  errorMessage: string | null;
}
